import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { ROUTES } from '../routes/Routes';
import { SEOMeta } from '../components/common/SEOMeta';
import { SectionTitle } from '../components/common/SectionTitle';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useCounter } from '../hooks/useCounter';
import { blogPosts } from '../constant/BlogPosts';
import '../assets/css/pages/Home.css';
import BlogItem from '../components/pages/BlogItem';

export default function Home() {
  const heroReveal = useScrollReveal();
  const statsReveal = useScrollReveal();
  const servicesReveal = useScrollReveal();
  const processReveal = useScrollReveal();
  const portfolioReveal = useScrollReveal();
  const whyReveal = useScrollReveal();
  const techReveal = useScrollReveal();
  const testReveal = useScrollReveal();

  const projectsCount = useCounter(150, 2000, statsReveal.isVisible);
  const clientsCount = useCounter(50, 2000, statsReveal.isVisible);
  const yearsCount = useCounter(10, 2000, statsReveal.isVisible);
  const satCount = useCounter(99, 2000, statsReveal.isVisible);

  // Testimonials auto-slide
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const startStrip = [
    { icon: "fa-solid fa-rocket", count: `${projectsCount}+`, label: "Projects Delivered" },
    { icon: "fa-solid fa-users", count: `${clientsCount}+`, label: "Happy Clients" },
    { icon: "fa-solid fa-calendar-check", count: `${yearsCount}+`, label: "Years Experience" },
    { icon: "fa-solid fa-star", count: `${satCount}+`, label: "Satisfaction" },
  ];
  const serviceList = [
    { icon: 'globe', title: 'Web Development', desc: 'Scalable applications using modern frameworks.' },
    { icon: 'mobile-screen', title: 'Mobile Apps', desc: 'Native & cross-platform mobile experiences.' },
    { icon: 'cloud', title: 'Cloud Solutions', desc: 'Secure cloud architecture and migration.' },
    { icon: 'pen-ruler', title: 'UI/UX Design', desc: 'Data-driven, intuitive interface design.' },
    { icon: 'shield-halved', title: 'Cybersecurity', desc: 'Enterprise-grade security protocols.' },
    { icon: 'robot', title: 'AI & ML', desc: 'Intelligent automation and data solutions.' }
  ];
  const processStep = [
    { icon: "fa-solid fa-magnifying-glass", title: "Discovery", subtitle: "We analyze your requirements" },
    { icon: "fa-solid fa-pen-ruler", title: "Design", subtitle: "We craft the blueprint" },
    { icon: "fa-solid fa-code", title: "Develop", subtitle: "We build with precision" },
    { icon: "fa-solid fa-rocket", title: "Deploy", subtitle: "We launch your product" },
  ];
  const portfolioList = [
    { cat: 'FinTech', name: 'FinTech Dashboard', icon: 'chart-line', tech: ['React', 'Node.js'], stripClass: 'strip-fintech' },
    { cat: 'Healthcare', name: 'HealthTrack App', icon: 'heart-pulse', tech: ['React Native', 'Firebase'], stripClass: 'strip-health' },
    { cat: 'E-Commerce', name: 'Nexus Retail', icon: 'cart-shopping', tech: ['Next.js', 'AWS'], stripClass: 'strip-ecom' },
    { cat: 'EdTech', name: 'EduLearn Platform', icon: 'graduation-cap', tech: ['Vue.js', 'Django'], stripClass: 'strip-edtech' },
    { cat: 'Crypto', name: 'CryptoWallet UI', icon: 'wallet', tech: ['Figma', 'UI/UX', 'Flutter'], stripClass: 'strip-crypto' },
    { cat: 'Logistics', name: 'LogiShip ERP', icon: 'truck-fast', tech: ['Angular', 'PostgreSQL'], stripClass: 'strip-logistic' },
  ];
  const features = [
    { icon: "fa-solid fa-bolt", title: "Agile Delivery", subtitle: "Rapid iteration and continuous deployment." },
    { icon: "fa-solid fa-shield-halved", title: "Secure by Design", subtitle: "Enterprise-grade security protocols standard." },
    { icon: "fa-solid fa-users-gear", title: "Expert Team", subtitle: "Senior engineers with deep domain knowledge." },
    { icon: "fa-solid fa-headset", title: "24/7 Support", subtitle: "Dedicated maintenance and monitoring." },
  ];
  const marqueeTechStack = [{ iconType: 'fa-brands', icon: 'react', name: 'React', color: '#61DAFB' },
  { iconType: 'fa-brands', icon: 'node-js', name: 'Node.js', color: '#6DA55F' },
  { iconType: 'fa-brands', icon: 'python', name: 'Python', color: '#3776AB' },
  { iconType: 'fa-brands', icon: 'docker', name: 'Docker', color: '#2496ED' },
  { iconType: 'fa-brands', icon: 'aws', name: 'AWS', color: '#FF9900' },
  { iconType: 'fa-brands', icon: 'swift', name: 'Swift', color: '#FA7343' },
  { iconType: 'fa-brands', icon: 'git-alt', name: 'Git', color: '#F1502F' },
  { iconType: 'fa-solid', icon: 'code', name: 'TypeScript', color: '#3178C6' },
  { iconType: 'fa-solid', icon: 'mobile-screen', name: 'Flutter', color: '#02569B' },
  { iconType: 'fa-brands', icon: 'react', name: 'React', color: '#61DAFB' },
  { iconType: 'fa-brands', icon: 'node-js', name: 'Node.js', color: '#6DA55F' },
  { iconType: 'fa-brands', icon: 'python', name: 'Python', color: '#3776AB' },
  { iconType: 'fa-brands', icon: 'docker', name: 'Docker', color: '#2496ED' },
  { iconType: 'fa-brands', icon: 'aws', name: 'AWS', color: '#FF9900' },
  { iconType: 'fa-brands', icon: 'swift', name: 'Swift', color: '#FA7343' },
  { iconType: 'fa-brands', icon: 'git-alt', name: 'Git', color: '#F1502F' },
  { iconType: 'fa-solid', icon: 'code', name: 'TypeScript', color: '#3178C6' },
  { iconType: 'fa-solid', icon: 'mobile-screen', name: 'Flutter', color: '#02569B' }];
  const marqueeTechStackReverse = [{ iconType: 'fa-brands', icon: 'angular', name: 'Angular', color: '#DD0031' },
  { iconType: 'fa-brands', icon: 'vuejs', name: 'Vue.js', color: '#42b883' },
  { iconType: 'fa-brands', icon: 'php', name: 'PHP', color: '#777BB4' },
  { iconType: 'fa-brands', icon: 'android', name: 'Android', color: '#3DDC84' },
  { iconType: 'fa-brands', icon: 'apple', name: 'Apple', color: '#999' },
  { iconType: 'fa-brands', icon: 'linux', name: 'Linux', color: '#FCC624' },
  { iconType: 'fa-brands', icon: 'java', name: 'Java', color: '#ED8B00' },
  { iconType: 'fa-brands', icon: 'figma', name: 'Figma', color: '#F24E1E' },
  { iconType: 'fa-solid', icon: 'k', name: 'Kotlin', color: '#7F52FF' },
  { iconType: 'fa-brands', icon: 'angular', name: 'Angular', color: '#DD0031' },
  { iconType: 'fa-brands', icon: 'vuejs', name: 'Vue.js', color: '#42b883' },
  { iconType: 'fa-brands', icon: 'php', name: 'PHP', color: '#777BB4' },
  { iconType: 'fa-brands', icon: 'android', name: 'Android', color: '#3DDC84' },
  { iconType: 'fa-brands', icon: 'apple', name: 'Apple', color: '#999' },
  { iconType: 'fa-brands', icon: 'linux', name: 'Linux', color: '#FCC624' },
  { iconType: 'fa-brands', icon: 'java', name: 'Java', color: '#ED8B00' },
  { iconType: 'fa-brands', icon: 'figma', name: 'Figma', color: '#F24E1E' },
  { iconType: 'fa-solid', icon: 'k', name: 'Kotlin', color: '#7F52FF' }];
  const testimonials = [
    { text: "Amazing team! I had the pleasure to work with Logicraft on several projects. I felt a real partnership, I felt safe in their talented hands and no doubt we will work again soon.", author: "Amichay Elnekave" },
    { text: "Highly impressed by Logicraft Infotech! Their professionalism, dedication, and quality service make them a trusted name in the IT industry. Great experience overall! 😄", author: "Maxwill Electronics" },
    { text: "Logicraft Infotech is a fantastic IT company with skilled developers and strong leadership. The work culture is positive, and everyone gets a chance to learn and improve. 😊", author: "Freedom Flour Mill" },
    { text: "Wonderful experience with Logicraft Infotech! Friendly staff, supportive management, and excellent project handling make this company stand out. Highly satisfied! 😍", author: "Tausif Raja", },
    { text: "🎉 A perfect place for career growth and quality IT services! Logicraft Infotech has an excellent team, great management, and a very positive atmosphere. Strongly recommended! 👍", author: "Inayat Vakaliya" },
    { text: "🚀 Great company with amazing opportunities! Logicraft Infotech supports employees, values teamwork, and creates a motivating environment for growth and success. Truly impressive! 👏", author: "MAHAMADHANIF KADIVAR"}
  ];
  const languageIcons = [
    'react', 'node-js', 'python', 'java', 'js', 'php', 'swift', 'android', 'apple', 'html5', 'css3-alt', 'git-alt', 'docker', 'aws', 'linux'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <>
      <SEOMeta
        title="Logicraft Infotech | Software Development Company"
        description="Logicraft Infotech - Crafting Digital Futures with cutting-edge web apps, mobile apps, and IT consulting."
        canonical="https://www.logicraftinfotech.com/"
      />

      <section className="hero" ref={heroReveal.ref}>
        <div className={`container hero-container ${heroReveal.className}`}>
          <div className="hero-content">
            <h1 className="hero-title">
              <span>Crafting Digital</span><br />
              <span className="gradient-text">Futures</span>
            </h1>
            <p className="hero-subtitle">
              We are a premium IT software development company delivering robust, scalable, and innovative digital solutions for forward-thinking businesses worldwide.
            </p>
            <div className="hero-buttons">
              <Link href={ROUTES.PORTFOLIO.path}>
                <button className="btn-solid">Explore Our Work</button>
              </Link>
              <Link href={ROUTES.CONTACT.path}>
                <button className="btn-outline">Start a Project</button>
              </Link>
            </div>
          </div>
          <div className="hero-icon-cloud">
            <div className="icon-cloud-inner glass-card">
              {languageIcons.map((icon, i) => (
                <i key={i} className={`fa-brands fa-${icon} float-icon i-${i}`}></i>
              ))}
              {['database', 'code', 'mobile-screen', 'cloud', 'server'].map((icon, i) => (
                <i key={`solid-${i}`} className={`fa-solid fa-${icon} float-icon s-${i}`}></i>
              ))}
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <i className="fa-solid fa-arrow-down"></i>
        </div>
      </section>

      <section className="stats-strip" ref={statsReveal.ref}>
        <div className={`container stats-grid ${statsReveal.className}`}>
          {startStrip.map((stat, i) => (
            <div className="stat-card" key={i}>
              <div className="stat-icon"><i className={stat.icon}></i></div>
              <div className="stat-number">{stat.count}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section" ref={servicesReveal.ref}>
        <div className={`container ${servicesReveal.className}`}>
          <SectionTitle title="Our Services" subtitle="Comprehensive IT solutions engineered for scale and performance." centered />
          <div className="services-hex-grid">
            {serviceList.map((srv, i) => (
              <div className="service-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="service-icon-circle">
                  <i className={`fa-solid fa-${srv.icon}`}></i>
                </div>
                <h3>{srv.title}</h3>
                <p>{srv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-dark" ref={processReveal.ref}>
        <div className={`container ${processReveal.className}`}>
          <SectionTitle title="How We Work" subtitle="A transparent, agile process ensuring on-time delivery." centered />
          <div className="process-timeline">
            {processStep.map((step, i) => (
              <div className="process-step" key={i}>
                <div className="step-circle">{i + 1}</div>
                <div className="step-icon"><i className={step.icon}></i></div>
                <h4>{step.title}</h4>
                <p>{step.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" ref={portfolioReveal.ref}>
        <div className={`container ${portfolioReveal.className}`}>
          <SectionTitle title="Featured Projects" subtitle="A glimpse into the digital solutions we've engineered." centered />
          <div className="portfolio-teaser-grid">
            {portfolioList.map((proj, i) => (
              <div className="portfolio-teaser-card" key={i}>
                <div className={`pt-strip ${proj.stripClass}`}>
                  <i className={`fa-solid fa-${proj.icon}`}></i>
                </div>
                <div className="pt-content">
                  <span className="pt-badge">{proj.cat}</span>
                  <h3 className="pt-name">{proj.name}</h3>
                  <div className="pt-tech">
                    {proj.tech.map(t => <span key={t}>{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-dark" ref={whyReveal.ref}>
        <div className={`container why-choose-container ${whyReveal.className}`}>
          <div className="why-text">
            <h2><span className="gradient-text">Why Partner</span> With Us?</h2>
            <div className="accent-line"></div>
            <p>We don't just write code; we build strategic digital assets. Our approach combines deep technical expertise with a profound understanding of business logic, ensuring your product scales flawlessly.</p>
            <p>Partner with engineers who treat your product like their own. Precision, scalability, and transparency are at the core of everything we do.</p>
            <Link href={ROUTES.ABOUT.path}>
              <button className="btn-solid" style={{ marginTop: '1rem' }}>Discover Our Culture</button>
            </Link>
          </div>
          <div className="why-features">
            {features.map((step, i) => (
              <div className="why-glass-card" key={i}>
                <div className="wg-icon"><i className={step.icon}></i></div>
                <h4>{step.title}</h4>
                <p>{step.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="blog-preview-section">
        <div className="container">
          <SectionTitle title="Latest Insights" subtitle="News, trends, and engineering perspectives from our team." centered />
          <div className="blog-grid-home">
            {blogPosts.slice(0, 3).map(post => {
              return (
                <BlogItem key={post.slug} post={post} />
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link href={ROUTES.BLOG.path} className="btn-outline">View All Articles →</Link>
          </div>
        </div>
      </section>

      <section className="marquee-section" ref={techReveal.ref}>
        <div className={`marquee-container ${techReveal.className}`}>
          <div className="marquee-label">Technologies We Master</div>
          <div className="marquee-track">
            {marqueeTechStack.map((item, i) => (
              <div className="tech-icon-pill" key={`t1-${i}`}>
                <i className={`${item.iconType} fa-${item.icon}`} style={{ color: item.color }}></i>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
          <div className="marquee-track reverse">
            {marqueeTechStackReverse.map((item, i) => (
              <div className="tech-icon-pill" key={`t2-${i}`}>
                <i className={`${item.iconType} fa-${item.icon}`} style={{ color: item.color }}></i>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="testimonials-section" ref={testReveal.ref}>
        <div className={`container testimonials-wrapper ${testReveal.className}`}>
          <div className="testimonials-header">
            <span className="testimonials-label">What Our Clients Say</span>
            <h2 className="testimonials-title">Client Success</h2>
          </div>
          <div className="testimonial-stage">
            {testimonials.map((t, i) => (
              <div className={`ts-slide ${i === currentTestimonial ? 'active' : ''}`} key={i}>
                <div className="ts-card">
                  <div className="ts-stars">
                    {[...Array(5)].map((_, si) => <i key={si} className="fa-solid fa-star"></i>)}
                  </div>
                  <p className="ts-quote">"{t.text}"</p>
                  <div className="ts-author-row">
                    <div className="ts-avatar">{t.author.charAt(0)}</div>
                    <h4 className="ts-name">{t.author}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="ts-controls">
            <button className="ts-arrow" onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}>
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <div className="ts-dots">
              {testimonials.map((_, i) => (
                <button key={i} className={`ts-dot ${i === currentTestimonial ? 'active' : ''}`} onClick={() => setCurrentTestimonial(i)} aria-label={`Go to slide ${i + 1}`}></button>
              ))}
            </div>
            <button className="ts-arrow" onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}>
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-circles"></div>
        <div className="container cta-content">
          <h2>Ready to build something <span className="gradient-text">extraordinary?</span></h2>
          <p>Let's discuss how our engineering team can accelerate your vision.</p>
          <div className="hero-buttons" style={{ justifyContent: 'center', marginTop: '2rem' }}>
            <Link href={ROUTES.CONTACT.path}>
              <button className="btn-solid">Start a Conversation</button>
            </Link>
            <Link href={ROUTES.PORTFOLIO.path}>
              <button className="btn-outline">View Portfolio</button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}